import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, MessageCircle, School, Smartphone } from "lucide-react";
import Reveal from "../components/ui/Reveal";
import IconBox from "../components/ui/IconBox";
import SEO, { generateBreadcrumbSchema, generateFAQSchema } from "../components/seo/SEO";
import { useAppDownload } from "../context/AppDownloadContext";

import { API_BASE_URL } from "../config/api";

const contactTypes = [
  {
    icon: Smartphone,
    title: "Download the App",
    description: "Get CampusGenie on your phone and start your journey to academic success.",
  },
  {
    icon: Briefcase,
    title: "Partnerships",
    description: "Interested in partnering with CampusGenie? Let's discuss how we can work together.",
  },
  {
    icon: School,
    title: "For Institutions",
    description: "Deploy CampusGenie at your university or school. We'll walk you through the process.",
  },
  {
    icon: MessageCircle,
    title: "General Inquiry",
    description: "Have questions about CampusGenie? We're here to help.",
  },
];

export default function ContactPage() {
  const { openAppModal } = useAppDownload();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    inquiryType: "General Inquiry",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!API_BASE_URL) {
      setSubmitError("Contact service is unavailable right now. Please try again later.");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const response = await fetch(`${API_BASE_URL}/contact/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          inquiry_type: formData.inquiryType,
          message: formData.message.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      setSubmitted(true);
      setFormData({
        name: "",
        email: "",
        inquiryType: "General Inquiry",
        message: "",
      });
    } catch (error) {
      setSubmitError("We couldn't send your message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqs = [
    { question: "How do I download CampusGenie?", answer: "CampusGenie is available on both iOS App Store and Google Play Store. Search for 'CampusGenie' or use the download links on our website." },
    { question: "Is CampusGenie available for my institution?", answer: "Yes! We offer institutional partnerships for universities, secondary schools, and EdTech companies. Contact us to schedule a demo and discuss deployment options." },
    { question: "How can I report a bug or suggest a feature?", answer: "Use the contact form on this page or email us at hello@campusgenie.app. We actively review all feedback and incorporate user suggestions into our roadmap." },
    { question: "Do you offer careers or internships?", answer: "We're always looking for talented people passionate about education technology. Send your resume to careers@campusgenie.app or check our About page for current openings." },
  ];

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Contact", url: "/contact" },
  ];

  const structuredData = [
    generateBreadcrumbSchema(breadcrumbs),
    generateFAQSchema(faqs),
  ];

  return (
    <>
      <SEO
        title="Contact Us - Download App, Partnerships & Support"
        description="Get in touch with CampusGenie. Download our student app, explore institutional partnerships, or reach out for support. We'd love to hear from you."
        url="/contact"
        structuredData={structuredData}
      />

      {/* Hero */}
      <section className="contact-hero">
        <div className="container">
          <Reveal>
            <span className="eyebrow">Contact Us</span>
            <h1 className="heading-hero" style={{ maxWidth: "14ch", margin: "0 auto 24px" }}>
              Let's start a conversation.
            </h1>
            <p className="body-large" style={{ maxWidth: "600px", margin: "0 auto" }}>
              Whether you're a student looking to download the app, an institution interested in partnership, or just want to say hello – we'd love to hear from you.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <div className="grid grid-4">
            {contactTypes.map((type, index) => (
              <Reveal key={type.title} delay={index * 0.1}>
                <div className="contact-info-card" style={{ textAlign: "center", height: "100%" }}>
                  <div style={{ marginBottom: "16px", display: "flex", justifyContent: "center", color: "var(--color-primary)" }}>
                    <IconBox icon={type.icon} size={40} />
                  </div>
                  <h3 className="heading-4" style={{ marginBottom: "8px" }}>{type.title}</h3>
                  <p className="body-small">{type.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section className="section section-alt">
        <div className="container">
          <Reveal>
            <div className="download-section">
              <h3 className="heading-2" style={{ color: "white" }}>Download CampusGenie</h3>
              <p style={{ color: "rgba(255,255,255,0.8)", marginBottom: "24px" }}>
                Available on iOS and Android. Start your journey to academic success today.
              </p>
              <div className="app-store-buttons" style={{ justifyContent: "center" }}>
                <a href="https://apps.apple.com/ng/app/campusgenie/id6739751652" className="app-store-btn" target="_blank" rel="noopener noreferrer">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <div className="app-store-btn-text">
                    <span>Download on the</span>
                    <span>App Store</span>
                  </div>
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.campusgenie.app" className="app-store-btn" target="_blank" rel="noopener noreferrer">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z"/>
                  </svg>
                  <div className="app-store-btn-text">
                    <span>Get it on</span>
                    <span>Google Play</span>
                  </div>
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Contact Form */}
      <section className="section">
        <div className="container">
          <div className="contact-grid">
            <Reveal>
              <div className="contact-form-card">
                <h3 className="heading-3" style={{ marginBottom: "24px" }}>Send us a message</h3>
                
                {submitted ? (
                  <div style={{ textAlign: "center", padding: "48px 24px" }}>
                    <div style={{ fontSize: "64px", marginBottom: "16px" }}>✅</div>
                    <h4 className="heading-4" style={{ marginBottom: "8px" }}>Message Sent!</h4>
                    <p className="body-base">We'll get back to you as soon as possible.</p>
                    <button 
                      className="btn btn-secondary"
                      onClick={() => {
                        setSubmitted(false);
                        setSubmitError("");
                      }}
                      style={{ marginTop: "24px" }}
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="form-group">
                      <label className="form-label" htmlFor="name">Your Name</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        className="form-input"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="John Doe"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label" htmlFor="email">Email Address</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="form-input"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@example.com"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label" htmlFor="type">Inquiry Type</label>
                      <select
                        id="type"
                        name="inquiryType"
                        className="form-input"
                        value={formData.inquiryType}
                        onChange={handleChange}
                      >
                        <option value="General Inquiry">General Inquiry</option>
                        <option value="Partnership">Partnership</option>
                        <option value="Institution Interest">Institution Interest</option>
                        <option value="Support">Support</option>
                        <option value="Feedback">Feedback</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label" htmlFor="message">Message</label>
                      <textarea
                        id="message"
                        name="message"
                        className="form-textarea"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder="Tell us what's on your mind..."
                      />
                    </div>
                    
                    {submitError ? (
                      <p className="body-small" style={{ color: "#dc2626", marginBottom: "16px" }}>
                        {submitError}
                      </p>
                    ) : null}

                    <button
                      type="submit"
                      className="btn btn-primary btn-lg"
                      style={{ width: "100%" }}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Sending..." : "Send Message"}
                    </button>
                  </form>
                )}
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="contact-info">
                <div className="contact-info-card">
                  <h3 className="heading-4">Email & Partnerships</h3>
                  <p>
                    <a href="mailto:hello@campusgenie.app" style={{ color: "var(--color-primary)" }}>
                      hello@campusgenie.app
                    </a>
                  </p>
                </div>
                
                <div className="contact-info-card">
                  <h3 className="heading-4">Support</h3>
                  <p>
                    Download the CampusGenie app, then contact support or submit a ticket through the app.
                  </p>
                </div>
                
                <div className="contact-info-card">
                  <h3 className="heading-4">Follow Us</h3>
                  <div style={{ display: "flex", gap: "12px", marginTop: "12px" }}>
                    <a 
                      href="https://twitter.com/campusgenie" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn btn-secondary btn-sm"
                    >
                      Twitter
                    </a>
                    <a 
                      href="https://instagram.com/campusgenie" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn btn-secondary btn-sm"
                    >
                      Instagram
                    </a>
                    <a 
                      href="https://linkedin.com/company/campusgenie" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn btn-secondary btn-sm"
                    >
                      LinkedIn
                    </a>
                  </div>
                </div>
                
                <div className="contact-info-card" style={{ 
                  background: "var(--color-primary-soft)",
                  borderColor: "var(--color-primary)"
                }}>
                  <h3 className="heading-4" style={{ color: "var(--color-brand-deep)" }}>Quick Start</h3>
                  <p style={{ marginBottom: "16px" }}>
                    Ready to get started? Download the app now and join thousands of students on their journey to success.
                  </p>
                  <button type="button" className="btn btn-primary btn-sm" onClick={openAppModal}>
                    Get the App
                  </button>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section section-alt">
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center">
              <span className="eyebrow">FAQs</span>
              <h2 className="heading-2">Common Questions</h2>
            </div>
          </Reveal>

          <div style={{ maxWidth: "800px", margin: "0 auto" }}>
            {[
              {
                q: "Is CampusGenie free to use?",
                a: "Yes! CampusGenie is free to download and use. We have premium features for power users, but the core experience is completely free.",
              },
              {
                q: "Which platforms is CampusGenie available on?",
                a: "CampusGenie is available on both iOS (App Store) and Android (Google Play). We also have a web version in development.",
              },
              {
                q: "How does the bridge from pre-uni to university work?",
                a: "When you get admission, you simply update your status in the app. CampusGenie automatically unlocks university features while keeping all your pre-uni progress and data intact.",
              },
              {
                q: "Can my school partner with CampusGenie?",
                a: "Absolutely! We work with universities and secondary schools to deploy CampusGenie as an official student platform. Contact us for more information.",
              },
              {
                q: "How do I get support if I have issues?",
                a: "Download the CampusGenie app, then contact support or submit a ticket through the app. We typically respond within 24 hours.",
              },
            ].map((faq, index) => (
              <Reveal key={faq.q} delay={index * 0.05}>
                <div style={{
                  padding: "24px",
                  background: "var(--color-surface-glass)",
                  border: "1px solid var(--color-border)",
                  borderRadius: "16px",
                  marginBottom: "16px",
                }}>
                  <h4 className="heading-4" style={{ marginBottom: "12px" }}>{faq.q}</h4>
                  <p className="body-base" style={{ margin: 0 }}>{faq.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
